import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Target, 
  Sparkles, 
  Download, 
  Loader2,
  Crown,
  RefreshCw,
  Calendar,
  Check,
  Plus,
  Lock,
  ChevronDown,
  AlertCircle,
  Gamepad2,
  Trophy,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

// Custom Dropdown Component with proper styling
const CustomDropdown = ({ 
  label, 
  icon: Icon, 
  value, 
  displayValue, 
  options, 
  onChange, 
  disabled = false,
  disabledMessage = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <label className="text-sm text-aura-text-secondary mb-2 block flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        {label}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-200 text-left ${
          disabled 
            ? 'bg-white/5 border-white/5 cursor-not-allowed opacity-50' 
            : isOpen 
              ? 'bg-aura-emerald/10 border-aura-emerald/30' 
              : 'bg-white/5 border-white/10 hover:border-aura-emerald/30'
        }`}
      >
        <span className={`truncate ${disabled ? 'text-aura-text-muted' : 'text-white'}`}>
          {displayValue || 'Select...'}
        </span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
          disabled ? 'text-aura-text-muted' : 'text-aura-text-secondary'
        } ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {disabled && disabledMessage && (
        <p className="text-xs text-aura-gold mt-1 flex items-center gap-1">
          <Lock className="w-3 h-3" />
          {disabledMessage}
        </p>
      )}

      <AnimatePresence>
        {isOpen && !disabled && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute z-50 w-full mt-2 py-2 rounded-xl bg-aura-surface border border-white/10 shadow-xl max-h-60 overflow-y-auto"
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left transition-colors duration-150 flex items-center justify-between ${
                    value === option.value 
                      ? 'bg-aura-emerald/20 text-aura-emerald' 
                      : 'text-white hover:bg-aura-emerald/10'
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {value === option.value && <Check className="w-4 h-4 flex-shrink-0" />}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const MANIFESTATION_MESSAGES = [
  "Aligned numbers. Clear intention. Let the universe meet disciplined analysis.",
  "AURA has done the math. Now hold the vision and trust the timing.",
  "Smart patterns guide the numbers. Your belief gives them momentum.",
  "Data finds the signal. Intention strengthens the outcome.",
  "Manifest with clarity. Play with discipline. Let AURA handle the rest.",
  "25+ strategies converged on these numbers. The alignment is deliberate.",
  "Every number tells a story. AURA found the pattern worth following.",
  "Statistical precision meets purposeful intention. This is how you manifest.",
  "AURA scanned a million simulated draws to surface these combinations.",
  "The numbers are set. Trust the process. Stay present.",
  "Calculated probability. Focused energy. Let both work in your favor.",
  "When data and intention align, extraordinary outcomes become possible.",
  "These numbers carry weight — statistical weight. Trust the analysis.",
  "AURA doesn't guess. It calculates. Your next move is intentional.",
  "The strongest patterns emerged. The rest is timing and trust.",
  "Every draw is a new opportunity. AURA ensures you enter prepared.",
  "Precision-crafted from historical patterns and forward-looking models.",
  "Your numbers are locked. Stay focused. Let the draw unfold.",
  "AURA's intelligence + your intention = a powerful combination.",
  "Statistical confidence meets personal conviction. Play smart.",
  "Numbers chosen with purpose. Backed by data. Amplified by belief.",
  "The universe rewards preparation. AURA made sure you're ready.",
  "Pattern recognition at scale. These numbers didn't happen by chance.",
  "Clear mind. Strong numbers. The rest is in motion.",
];


const MatchColorLegend = () => (
  <div className="mt-8 flex items-center justify-center gap-6 py-3 px-5 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="match-color-legend">
    <span className="text-xs text-aura-text-muted uppercase tracking-wider mr-1">Match Key</span>
    <span className="flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#10B981', boxShadow: '0 0 6px rgba(16,185,129,0.6)' }} />
      <span className="text-xs text-aura-text-secondary">Main Number</span>
    </span>
    <span className="flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#a855f7', boxShadow: '0 0 6px rgba(168,85,247,0.6)' }} />
      <span className="text-xs text-aura-text-secondary">Supplementary</span>
    </span>
    <span className="flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ef4444', boxShadow: '0 0 6px rgba(239,68,68,0.6)' }} />
      <span className="text-xs text-aura-text-secondary">Powerball</span>
    </span>
  </div>
);

const PredictionAccordion = ({ lotteryKey, label, items, navigate, formatDate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const totalGames = items.reduce((s, p) => s + (p.numbers?.length || 0), 0);
  const verifiedCount = items.filter(p => p.verified).length;
  const totalMatches = items.reduce((s, p) => {
    return s + (p.games_results || []).reduce((gs, gr) => gs + (gr?.match_count || 0), 0);
  }, 0);
  const winnerCount = items.reduce((s, p) => {
    return s + (p.games_results || []).filter(gr => gr?.division != null).length;
  }, 0);

  const colorMap = {
    oz_lotto: 'from-blue-500/20 to-blue-900/10 border-blue-500/30',
    tatts_lotto: 'from-purple-500/20 to-purple-900/10 border-purple-500/30',
    weekday_windfall: 'from-teal-500/20 to-teal-900/10 border-teal-500/30',
    powerball: 'from-red-500/20 to-red-900/10 border-red-500/30',
  };

  return (
    <div className={`rounded-xl border bg-gradient-to-r ${colorMap[lotteryKey] || 'from-white/5 to-transparent border-white/10'} overflow-hidden`} data-testid={`pred-group-${lotteryKey}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
        data-testid={`pred-accordion-${lotteryKey}`}
      >
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-white/70" />
          <span className="font-heading text-lg font-semibold text-white capitalize">{label}</span>
          <span className="text-sm text-aura-text-muted">({items.length} prediction{items.length !== 1 ? 's' : ''}, {totalGames} game{totalGames !== 1 ? 's' : ''})</span>
          {winnerCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-aura-gold/20 text-aura-gold" data-testid={`pred-winners-${lotteryKey}`}>{winnerCount} winner{winnerCount !== 1 ? 's' : ''}</span>
          )}
          {verifiedCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-aura-emerald/20 text-aura-emerald">{verifiedCount} verified</span>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-aura-text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-white/5 divide-y divide-white/5">
          {items.sort((a, b) => (b.draw_date || b.created_at || '').localeCompare(a.draw_date || a.created_at || '')).map((pred) => (
            <div key={pred.prediction_id} className="p-4 hover:bg-white/5 transition-colors" data-testid={`pred-row-${pred.prediction_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${pred.verified ? 'bg-aura-emerald/20' : 'bg-white/5'}`}>
                      {pred.verified ? <Check className="w-4 h-4 text-aura-emerald" /> : <Target className="w-4 h-4 text-aura-text-muted" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white font-medium">
                          {pred.draw_date ? formatDate(pred.draw_date) : formatDate(pred.created_at)}
                        </span>
                        {pred.verified && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-aura-emerald/20 text-aura-emerald">Verified</span>
                        )}
                      </div>
                      <div className="text-xs text-aura-text-muted mt-0.5">
                        {pred.numbers?.length || 0} game{(pred.numbers?.length || 0) !== 1 ? 's' : ''} generated
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    {pred.numbers.map((nums, gameIdx) => {
                      const gameResult = pred.games_results?.[gameIdx];
                      const matchedNums = gameResult?.matched_numbers || [];
                      const matchedSupps = gameResult?.matched_supps || [];
                      const pbMatch = gameResult?.powerball_match || false;
                      const hasMatches = (gameResult?.match_count || 0) > 0;
                      const isWinner = gameResult?.division != null;
                      const winAmount = gameResult?.winnings || 0;
                      return (
                        <div key={gameIdx} className={`flex flex-col sm:flex-row sm:items-center gap-2 ${isWinner ? 'bg-aura-emerald/5 rounded-lg p-2 -mx-2' : ''}`}>
                          <span className="text-xs text-aura-text-muted w-14 flex-shrink-0">Game {gameIdx + 1}:</span>
                          <div className="flex gap-1 flex-wrap">
                            {nums.map((num, i) => {
                              const isMatched = matchedNums.includes(num);
                              const isSuppMatch = matchedSupps.includes(num);
                              return (
                                <span key={i} className={`lottery-ball small ${isMatched ? 'winner' : isSuppMatch ? 'supp-match' : ''}`}>{num}</span>
                              );
                            })}
                            {pred.powerball && pred.powerball[gameIdx] && (
                              <span className={`lottery-ball powerball small ml-1 ${pbMatch ? 'winner' : ''}`}>{pred.powerball[gameIdx]}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 ml-1">
                            {isWinner ? (
                              <>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-aura-gold/20 text-aura-gold font-medium">WINNER</span>
                                <span className="text-xs px-2 py-0.5 rounded bg-aura-emerald/10 text-aura-emerald border border-aura-emerald/20">Div {gameResult.division}</span>
                                {/* Always show the exact official payout next to the Div badge — mirrors Games Tracking. */}
                                <span className="text-xs text-aura-gold font-medium">Won ${winAmount.toFixed(2)}</span>
                              </>
                            ) : hasMatches ? (
                              <span className="text-xs text-aura-emerald">{gameResult.match_count} match{gameResult.match_count !== 1 ? 'es' : ''}</span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-aura-emerald/20 text-aura-emerald hover:bg-aura-emerald/10 flex-1 sm:flex-initial"
                    onClick={() => navigate("/tickets", { state: { predictions: pred, lotteryType: pred.lottery_type, drawDate: pred.draw_date || pred.created_at.split('T')[0] } })}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add to Games
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-aura-text-muted hover:text-red-400 h-8 w-8"
                    onClick={() => onDelete(pred.prediction_id)}
                    data-testid={`delete-pred-${pred.prediction_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Client-side fallback: compute the next 21 upcoming draw dates immediately so
// the Draw Date dropdown renders instantly while the backend's authoritative
// /available-draws filter (which removes draws whose results already exist)
// arrives in the background. Mirrors backend LOTTERY_SCHEDULE.
// Mon=Windfall, Tue=Oz Lotto, Wed=Windfall, Thu=Powerball, Fri=Windfall, Sat=Tatts.
const CLIENT_LOTTERY_SCHEDULE = {
  0: { lottery_type: "weekday_windfall", lottery_name: "Weekday Windfall" },
  1: { lottery_type: "oz_lotto",         lottery_name: "Oz Lotto" },
  2: { lottery_type: "weekday_windfall", lottery_name: "Weekday Windfall" },
  3: { lottery_type: "powerball",        lottery_name: "Powerball" },
  4: { lottery_type: "weekday_windfall", lottery_name: "Weekday Windfall" },
  5: { lottery_type: "tatts_lotto",      lottery_name: "Tatts Lotto" },
};
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const computeUpcomingDraws = () => {
  const draws = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 21; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    // JS getDay(): Sun=0..Sat=6. Convert to Mon=0..Sun=6 to match backend.
    const wd = (d.getDay() + 6) % 7;
    const cfg = CLIENT_LOTTERY_SCHEDULE[wd];
    if (!cfg) continue; // Sunday
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    draws.push({
      date: `${yyyy}-${mm}-${dd}`,
      label: `${DAY_NAMES[wd]}, ${dd}.${mm}.${yyyy}`,
      lottery_type: cfg.lottery_type,
      lottery_name: cfg.lottery_name,
    });
  }
  return draws;
};

const PredictionsPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [lotteryType, setLotteryType] = useState("");
  const [numGames, setNumGames] = useState(1);
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lotteryTypes, setLotteryTypes] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [ticketNumbers, setTicketNumbers] = useState([]);
  const [weeklyUsage, setWeeklyUsage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  // Seed with client-side computed draws so the dropdown is interactive from
  // the very first render. Uses React's lazy-initial-state form so the helper
  // runs synchronously exactly once on mount — no chance of an intermediate
  // empty value, no dependency on useMemo timing. The backend call below
  // refines this list with the server-filtered version (which strips draws
  // whose results already exist).
  const [availableDraws, setAvailableDraws] = useState(() => computeUpcomingDraws());
  const [manifestationMessage, setManifestationMessage] = useState(
    MANIFESTATION_MESSAGES[Math.floor(Math.random() * MANIFESTATION_MESSAGES.length)]
  );

  const isPro = user?.email === "ai.manifest.official@gmail.com" || user?.subscription_tier === "pro" || user?.subscription_tier === "premium";
  const hasReachedFreeLimit = !isPro && weeklyUsage >= 1;

  // Derive lottery type from selected date using available draws from backend
  const selectedDraw = availableDraws.find(d => d.date === selectedDate);
  const validLotteryForDate = selectedDraw?.lottery_type || null;

  useEffect(() => {
    // Pre-populate selectedDate from the client-side computed list so the
    // dropdown's "selected" value and the auto-selected lottery are set on
    // first render. fetchAvailableDraws() will refine this with the backend's
    // open-draws filter if needed.
    if (!selectedDate) {
      const seed = computeUpcomingDraws();
      if (seed.length > 0) {
        setSelectedDate(seed[0].date);
        setLotteryType(seed[0].lottery_type);
      }
    }
    fetchLotteryTypes();
    fetchHistory();
    fetchTicketNumbers();
    fetchWeeklyUsage();
    fetchAvailableDraws();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select lottery when date changes
  useEffect(() => {
    if (selectedDate && validLotteryForDate) {
      setLotteryType(validLotteryForDate);
    }
  }, [selectedDate, validLotteryForDate]);

  const fetchAvailableDraws = async () => {
    try {
      const response = await axios.get(`${API}/available-draws`);
      const draws = response.data.draws || [];
      if (draws.length === 0) return; // keep the client-computed list as a safety net
      setAvailableDraws(draws);
      // Only auto-select if the user hasn't picked anything yet, OR if the
      // currently-selected date is no longer in the server-filtered list
      // (e.g. results just dropped for that draw — switch to next open one).
      setSelectedDate((prev) => {
        if (prev && draws.some((d) => d.date === prev)) return prev;
        const next = draws[0]?.date || "";
        if (next) setLotteryType(draws[0].lottery_type);
        return next;
      });
    } catch (error) {
      console.error("Failed to fetch available draws");
    }
  };

  const fetchLotteryTypes = async () => {
    try {
      const response = await axios.get(`${API}/lottery-types`);
      setLotteryTypes(response.data.types);
      // Don't auto-select - let date validation handle it
    } catch (error) {
      console.error("Failed to fetch lottery types");
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/predictions`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch history");
    }
  };

  const fetchTicketNumbers = async () => {
    try {
      const response = await axios.get(`${API}/tickets`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const allNumbers = response.data.flatMap(t => t.numbers || []);
      setTicketNumbers(allNumbers);
    } catch (error) {
      console.error("Failed to fetch ticket numbers");
    }
  };

  const fetchWeeklyUsage = async () => {
    try {
      const response = await axios.get(`${API}/predictions/weekly-usage`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setWeeklyUsage(response.data.games_generated || 0);
    } catch (error) {
      // Fallback to user data if endpoint doesn't exist
      setWeeklyUsage(user?.predictions_this_week || 0);
    }
  };

  const deletePrediction = async (predictionId) => {
    try {
      await axios.delete(`${API}/predictions/${predictionId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setHistory(prev => prev.filter(p => p.prediction_id !== predictionId));
    } catch (error) {
      console.error("Failed to delete prediction");
    }
  };

  const formatDate = (dateStr) => {
    // Parse as local date to avoid UTC timezone offset issues
    const parts = dateStr.split('-');
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const day = days[date.getDay()];
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${day}, ${dd}.${mm}.${yyyy}`;
  };

  const generatePredictions = async () => {
    // Validate free user limits
    if (!isPro && weeklyUsage >= 1) {
      toast.error("You've reached your free weekly limit. Upgrade to AURA PRO for unlimited predictions.");
      return;
    }

    // Free users can only generate 1 game
    const gamesToGenerate = isPro ? numGames : 1;

    if (!lotteryType) {
      toast.error("Please select a lottery type");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/generate-predictions`,
        { 
          lottery_type: lotteryType, 
          num_games: gamesToGenerate,
          draw_date: selectedDate
        },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      setPredictions(response.data);
      setIsAnimating(true);
      setManifestationMessage(MANIFESTATION_MESSAGES[Math.floor(Math.random() * MANIFESTATION_MESSAGES.length)]);
      setTimeout(() => setIsAnimating(false), 2000);
      
      // Update weekly usage
      if (!isPro) {
        setWeeklyUsage(prev => prev + 1);
      }
      
      toast.success(`${gamesToGenerate} prediction${gamesToGenerate > 1 ? 's' : ''} generated successfully!`);
      fetchHistory();
    } catch (error) {
      if (error.response?.status === 403) {
        const message = error.response?.data?.detail || "Upgrade to AURA PRO for unlimited predictions!";
        toast.error(message);
        if (message.includes("Upgrade") || message.includes("limit")) {
          setWeeklyUsage(1); // Mark as limit reached
        }
      } else {
        toast.error("Failed to generate predictions");
      }
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    if (!predictions) return;
    
    const content = `
AI Manifest - AURA Predictions
Generated: ${new Date().toLocaleString()}
Draw Date: ${formatDate(selectedDate)}
Lottery: ${lotteryType.replace('_', ' ').toUpperCase()}

Numbers:
${predictions.numbers.map((nums, i) => `Game ${i + 1}: ${nums.join(' - ')}${predictions.powerball ? ` | PB: ${predictions.powerball[i]}` : ''}`).join('\n')}

Strategies Used:
${predictions.strategies_used.join(', ')}

---
This platform is an AI analysis tool. Always gamble responsibly.
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aura_predictions_${selectedDate}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Predictions exported!");
  };

  const isNumberInTicket = (num) => ticketNumbers.includes(num);

  // Date options come from the backend - already filtered for closed draws
  const dateOptions = availableDraws.map(draw => ({
    value: draw.date,
    label: draw.label
  }));

  // Lottery game options - only show valid lottery for selected date
  const lotteryOptions = lotteryTypes
    .filter(type => !validLotteryForDate || type.id === validLotteryForDate)
    .map(type => ({
      value: type.id,
      label: type.name
    }));

  // Number of games options (1-8 for PRO, only 1 for free)
  const gameCountOptions = isPro 
    ? Array.from({ length: 50 }, (_, i) => i + 1).map(n => ({ value: n.toString(), label: `${n} Game${n > 1 ? 's' : ''}` }))
    : [{ value: "1", label: "1 Game" }];

  const selectedLottery = lotteryTypes.find(l => l.id === lotteryType);

  return (
    <DashboardLayout currentPage="predictions">
      <div className="p-4 sm:p-6 max-w-6xl w-full mx-auto overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">AI Predictions</h1>
            <p className="text-aura-text-secondary">Generate Lottery Predictions</p>
          </div>
          <div className="flex items-center gap-3">
            {isPro ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-aura-emerald/10 border border-aura-emerald/20">
                <Crown className="w-4 h-4 text-aura-emerald" />
                <span className="text-sm text-aura-emerald">PRO Unlimited</span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-aura-gold/10 border border-aura-gold/20">
                <span className="text-sm text-aura-gold">{weeklyUsage}/1 Free This Week</span>
              </div>
            )}
          </div>
        </div>

        {/* Free Limit Reached Banner */}
        {hasReachedFreeLimit && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-aura p-6 mb-8 border-aura-gold/30 bg-gradient-to-r from-aura-gold/5 to-transparent"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-aura-gold/10 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-aura-gold" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white">Weekly Limit Reached</h3>
                  <p className="text-aura-text-secondary text-sm">
                    You've reached your free weekly limit. Upgrade to AURA PRO for unlimited predictions.
                  </p>
                </div>
              </div>
              <Button
                className="btn-gold"
                onClick={() => navigate("/subscription")}
                data-testid="upgrade-predictions-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to PRO - $3.50/week
              </Button>
            </div>
          </motion.div>
        )}

        {/* PRO Benefits Banner for free users who haven't hit limit */}
        {!isPro && !hasReachedFreeLimit && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-aura p-4 mb-8 border-white/5"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Crown className="w-5 h-5 text-aura-gold" />
                <span className="text-sm text-aura-text-secondary">
                  Free users get 1 game per week. <span className="text-aura-gold">Upgrade to PRO</span> for 1-8 games per draw!
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-aura-gold/20 text-aura-gold hover:bg-aura-gold/10"
                onClick={() => navigate("/subscription")}
              >
                Upgrade
              </Button>
            </div>
          </motion.div>
        )}

        {/* Generator Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-aura p-8 mb-8" style={{ overflow: 'visible' }}
        >
          <div className="grid md:grid-cols-4 gap-6 mb-8" style={{ overflow: 'visible' }}>
            {/* Date Selector */}
            <CustomDropdown
              label="Draw Date"
              icon={Calendar}
              value={selectedDate}
              displayValue={selectedDate ? formatDate(selectedDate) : 'Select date'}
              options={dateOptions}
              onChange={setSelectedDate}
              disabled={hasReachedFreeLimit}
              disabledMessage="Upgrade to continue"
            />

            {/* Lottery Type - Auto-selected based on date */}
            <div>
              <label className="text-sm text-aura-text-secondary mb-2 block flex items-center gap-2">
                <Gamepad2 className="w-4 h-4" />
                Lottery Game
              </label>
              <div className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-200 text-left ${
                hasReachedFreeLimit 
                  ? 'bg-white/5 border-white/5 cursor-not-allowed opacity-50' 
                  : 'bg-aura-emerald/10 border-aura-emerald/30'
              }`}>
                <span className="text-white truncate">
                  {selectedLottery?.name || lotteryType?.replace('_', ' ') || 'Select date first'}
                </span>
                <Lock className="w-4 h-4 text-aura-emerald flex-shrink-0" />
              </div>
              <p className="text-xs text-aura-emerald mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Auto-selected based on draw date
              </p>
            </div>

            {/* Number of Games */}
            <CustomDropdown
              label="Number of Games"
              icon={Target}
              value={numGames.toString()}
              displayValue={`${numGames} Game${numGames > 1 ? 's' : ''}`}
              options={gameCountOptions}
              onChange={(v) => setNumGames(parseInt(v))}
              disabled={!isPro || hasReachedFreeLimit}
              disabledMessage={hasReachedFreeLimit ? "Upgrade to continue" : "PRO only (1-8 games)"}
            />

            {/* Generate Button */}
            <div className="flex flex-col justify-end">
              <label className="text-sm text-aura-text-secondary mb-2 block opacity-0">Action</label>
              <Button 
                className={`w-full py-6 ${hasReachedFreeLimit ? 'bg-white/10 text-aura-text-muted cursor-not-allowed' : 'btn-primary'}`}
                onClick={generatePredictions}
                disabled={loading || hasReachedFreeLimit}
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : hasReachedFreeLimit ? (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Limit Reached
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Selected Lottery Info */}
          {selectedLottery && !hasReachedFreeLimit && (
            <div className="text-sm text-aura-text-muted mb-4">
              {selectedLottery.description}
            </div>
          )}

          {/* Results */}
          {predictions && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-white/10 pt-8"
            >
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-white">Your Numbers</h3>
                  <p className="text-sm text-aura-text-muted mt-1">
                    For {formatDate(selectedDate)} • {lotteryType.replace('_', ' ').toUpperCase()}
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-white/10 text-aura-text-secondary hover:text-white"
                    onClick={generatePredictions}
                    disabled={loading || hasReachedFreeLimit}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-white/10 text-aura-text-secondary hover:text-white"
                    onClick={exportToPDF}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                  <Button 
                    size="sm"
                    className="btn-gold"
                    onClick={() => navigate("/tickets", { state: { predictions: predictions, lotteryType, drawDate: selectedDate } })}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add to Games
                  </Button>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {predictions.numbers.map((nums, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.15 }}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/5"
                  >
                    <span className="text-aura-text-muted font-mono text-sm w-20 flex-shrink-0">Game {index + 1}</span>
                    <div className="lottery-numbers-grid">
                      {nums.map((num, i) => (
                        <span 
                          key={i}
                          className={`lottery-ball number-ball ${isAnimating ? 'rolling' : ''}`}
                          style={{ animationDelay: `${(index * nums.length + i) * 0.08}s` }}
                          data-testid={`prediction-ball-${index}-${i}`}
                        >
                          {num}
                        </span>
                      ))}
                      {predictions.powerball && (
                        <span 
                          className={`lottery-ball powerball number-ball ml-1 ${isAnimating ? 'rolling' : ''}`}
                          style={{ animationDelay: `${(index * nums.length + nums.length) * 0.08}s` }}
                          data-testid={`prediction-pb-${index}`}
                        >
                          {predictions.powerball[index]}
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Manifestation Message */}
              <div className="rounded-xl bg-aura-emerald/5 border border-aura-emerald/20 p-5 text-center">
                <p className="text-aura-text-secondary text-sm italic leading-relaxed">
                  "{manifestationMessage}"
                </p>
                <p className="text-aura-emerald/50 text-xs mt-2 uppercase tracking-wider">AURA Intelligence</p>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* History - Grouped by Lottery */}
        {history.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-semibold text-white mb-4">Previous Predictions</h2>
            <div className="space-y-4">
              {(() => {
                const lotteryGroups = {
                  oz_lotto: { label: "Oz Lotto", items: [] },
                  tatts_lotto: { label: "Tatts Lotto", items: [] },
                  weekday_windfall: { label: "Weekday Windfall", items: [] },
                  powerball: { label: "Powerball", items: [] },
                };
                history.slice(0, 20).forEach(p => {
                  const group = lotteryGroups[p.lottery_type];
                  if (group) group.items.push(p);
                  else {
                    if (!lotteryGroups["other"]) lotteryGroups["other"] = { label: "Other", items: [] };
                    lotteryGroups["other"].items.push(p);
                  }
                });
                return Object.entries(lotteryGroups)
                  .filter(([, g]) => g.items.length > 0)
                  .map(([key, group]) => (
                    <PredictionAccordion key={key} lotteryKey={key} label={group.label} items={group.items} navigate={navigate} formatDate={formatDate} onDelete={deletePrediction} />
                  ));
              })()}
            </div>
            <MatchColorLegend />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PredictionsPage;
